/**
 * ErrorBoundary Component (Task 10.8)
 *
 * Catches component errors and displays user-friendly error messages
 * Provides recovery actions (reload, reset)
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, RefreshCw, RotateCcw } from 'lucide-react';

interface ErrorBoundaryProps {
  /**
   * Child components to render
   */
  children: ReactNode;

  /**
   * Optional fallback UI to render instead of default error display
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;

  /**
   * Optional callback when error is caught
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;

  /**
   * Optional reset callback for custom reset logic
   */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught error:', error, errorInfo);

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset error boundary state
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  /**
   * Reload the entire page
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default error UI
      return (
        <motion.div
          className="flex h-full w-full items-center justify-center p-8"
          style={{ backgroundColor: 'var(--color-dark-bg)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div
            className="max-w-md rounded-lg border p-6"
            style={{
              backgroundColor: 'var(--color-dark-panel)',
              borderColor: 'var(--color-body-control)',
            }}
          >
            {/* Error Icon */}
            <div className="mb-4 flex items-center gap-3">
              <AlertCircle
                className="h-8 w-8"
                style={{ color: 'var(--color-body-control)' }}
              />
              <h2
                className="text-xl font-semibold"
                style={{ color: 'var(--color-dark-text)' }}
              >
                Something went wrong
              </h2>
            </div>

            {/* Error Message */}
            <p
              className="mb-4 text-sm"
              style={{ color: 'var(--color-dark-text-muted)' }}
            >
              An unexpected error occurred. You can try to recover by resetting
              the component or reloading the page.
            </p>

            {/* Error Details (in development) */}
            {import.meta.env.DEV && (
              <details className="mb-4">
                <summary
                  className="cursor-pointer text-xs font-medium"
                  style={{ color: 'var(--color-dark-text-muted)' }}
                >
                  Error details
                </summary>
                <pre
                  className="mt-2 overflow-auto rounded p-2 text-xs"
                  style={{
                    backgroundColor: 'var(--color-dark-bg)',
                    color: 'var(--color-body-control)',
                  }}
                >
                  {this.state.error.message}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            {/* Recovery Actions */}
            <div className="flex gap-3">
              <motion.button
                onClick={this.handleReset}
                className="flex items-center gap-2 rounded px-4 py-2 text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-body-head)',
                  color: '#fff',
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </motion.button>

              <motion.button
                onClick={this.handleReload}
                className="flex items-center gap-2 rounded border px-4 py-2 text-sm font-medium transition-colors"
                style={{
                  borderColor: 'var(--color-dark-border)',
                  color: 'var(--color-dark-text)',
                }}
                whileHover={{
                  scale: 1.02,
                  backgroundColor: 'var(--color-dark-bg)',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </motion.button>
            </div>
          </div>
        </motion.div>
      );
    }

    return this.props.children;
  }
}
